// One Team entry: a Card meant to be a child of the tab's joined CardGroup. Reproduces the
// PR #6802 prototype WorkerIdentity row — avatar • name • home unit + role • charge-eligible
// note • employment/shift-type/FTE meta line — plus the "Show available shifts" toggle that
// rings the worker's open shifts in the grid. The avatar turns grass-green while that
// worker's shifts are highlighted.
import React from 'react';
import { Tooltip } from 'antd';
import {
  Button,
  Card,
  Gap,
  Gutter,
  Text,
  UserAvatar,
} from '@common/components';
import { Colors } from '@common/tokens';
import type {
  ScheduleDragStateType,
  ScheduleTeamMemberType,
} from '@load-balancing/types';
import { startDragGhost } from '@load-balancing/services';
import { ScheduleDragHandle } from '@load-balancing/components/schedule-drag-handle';
import { ScheduleHomeUnit } from '@load-balancing/components/schedule-home-unit';
import {
  getEmploymentLabel,
  getFteLabel,
  getFteTooltip,
  getShiftPatternLabel,
  getShiftTypeLabel,
  isBelowFte,
} from './_schedule-review-team-tab-service';

type Props = {
  member: ScheduleTeamMemberType;
  highlightedWorkerId?: string | null;
  onHighlightWorker?: (workerId: string | null) => void;
  assignedHighlightWorkerId?: string | null;
  onHighlightAssigned?: (workerId: string | null) => void;
  /**
   * Whether this worker has any shift assigned in the schedule currently under review.
   * The "Assigned shifts" toggle only renders when true — a cross-unit worker with no
   * shifts in this schedule has nothing to highlight (WORK-6529 QA).
   */
  hasAssignedShifts?: boolean;
  /**
   * Whether this worker has any eligible open shift to fill in the schedule under review.
   * The "Eligible shifts" toggle only renders when true — a below-FTE worker with no
   * eligible open shifts has nothing to ring, so the toggle would otherwise do nothing
   * (WORK-6551 QA).
   */
  hasEligibleShifts?: boolean;
  /** Open this worker's profile panel (clicking their name). */
  onWorkerSelect?: (workerId: string) => void;
  /** Drag this worker onto an open grid slot (a new assignment). */
  onWorkerDragStart?: (state: ScheduleDragStateType) => void;
  onWorkerDragEnd?: () => void;
};

export function ScheduleReviewTeamTabCard({
  member,
  highlightedWorkerId = null,
  onHighlightWorker,
  assignedHighlightWorkerId = null,
  onHighlightAssigned,
  hasAssignedShifts = false,
  hasEligibleShifts = false,
  onWorkerSelect,
  onWorkerDragStart,
  onWorkerDragEnd,
}: Props) {
  const isHighlighted = highlightedWorkerId === member.id;
  const isAssignedHighlighted = assignedHighlightWorkerId === member.id;
  const showChargeEligible = member.chargeEligible;
  // Below-FTE workers are the ones worth slotting into open shifts, so only they get the eligible
  // toggle — and only when they actually have an eligible open shift to ring; otherwise the toggle
  // would highlight the worker but navigate/ring nothing (WORK-6551 QA).
  const canHighlightEligible =
    Boolean(onHighlightWorker) && isBelowFte(member) && hasEligibleShifts;
  // Only offer the assigned-shift highlight when the worker actually has shifts in this
  // schedule; otherwise the toggle would ring nothing (e.g. a cross-unit visitor).
  const canHighlightAssigned =
    Boolean(onHighlightAssigned) && hasAssignedShifts;
  // Hover detail for the FTE readout (Expected vs Scheduled hours); null hides the tooltip.
  const fteTooltip = getFteTooltip(member);

  const handleDragStart = (event: React.DragEvent) => {
    startDragGhost(event, member.name);
    onWorkerDragStart?.({
      workerId: member.id,
      workerName: member.name,
      source: 'roster',
    });
  };

  const handleDragEnd = () => onWorkerDragEnd?.();

  return (
    <Card className="schedule-review-team-tab-card">
      <div
        className="sd-team-card"
        draggable={onWorkerDragStart ? true : undefined}
        style={onWorkerDragStart ? { cursor: 'grab' } : undefined}
        onDragStart={onWorkerDragStart ? handleDragStart : undefined}
        onDragEnd={onWorkerDragStart ? handleDragEnd : undefined}
      >
        <Gap size="sm" align="start" fullWidth>
          <Gap vertical size="md" align="start">
            <UserAvatar
              size={34}
              uppercase
              textAsInitials={member.name}
              backgroundColor={
                isHighlighted ? Colors.grassLightest : Colors.steelLighter
              }
              textColor={
                isHighlighted ? Colors.grassMid : Colors.midnightRegular
              }
              borderColor={isHighlighted ? Colors.grass : Colors.steel}
            />
            {onWorkerDragStart && (
              <ScheduleDragHandle revealOnHoverOf="sd-team-card" />
            )}
          </Gap>

          <Gutter direction="top" size="sm">
            <Gap vertical size="xxs" align="start">
              {onWorkerSelect ? (
                <Button
                  kind="link"
                  onClick={(event) => {
                    event.stopPropagation();
                    onWorkerSelect(member.id);
                  }}
                >
                  {member.name}
                </Button>
              ) : (
                <Text size="lg" color="midnightDarkest">
                  {member.name}
                </Text>
              )}

              <ScheduleHomeUnit
                unitName={member.unit}
                roleAbbreviation={member.role}
              />

              {showChargeEligible && (
                <Text size="md" color="midnightLighter">
                  Charge eligible
                </Text>
              )}

              <Gap size="xs" align="center" wrap>
                <Text size="md" color="midnightLighter">
                  {getEmploymentLabel(member)}
                </Text>
                <Text size="md" color="midnightLighter">
                  {' ∙ '}
                  {getShiftTypeLabel(member)}
                </Text>
                <Text size="md" color="midnightLighter">
                  {' ∙ '}
                  {getShiftPatternLabel(member)}
                </Text>
                {/* FTE is only ever shown for home-unit workers. Cross-unit workers
                    (homeUnitMember: false) never have it, so hide the separator dot and
                    the label entirely rather than showing a "—" placeholder (a home-unit
                    worker whose FTE hasn't computed yet still shows "—" — see getFteLabel). */}
                {member.homeUnitMember && (
                  <>
                    <Text size="lg">·</Text>
                    <Tooltip
                      title={
                        fteTooltip && (
                          <Text size="md" color="white">
                            {fteTooltip}
                          </Text>
                        )
                      }
                    >
                      {/* A host span keeps the antd Tooltip's ref target — Text alone
                            does not forward refs (mirrors the Health Strip chip). */}
                      <span>
                        <Text size="md" color="midnightRegular">
                          {getFteLabel(member)}
                        </Text>
                      </span>
                    </Tooltip>
                  </>
                )}
              </Gap>

              {(canHighlightAssigned || canHighlightEligible) && (
                <Gutter direction="top" size="sm">
                  <Gap size="sm">
                    {canHighlightAssigned && (
                      <Button
                        kind={isAssignedHighlighted ? 'primary' : 'secondary'}
                        size="sm"
                        onClick={(event) => {
                          event.stopPropagation();
                          onHighlightAssigned?.(
                            isAssignedHighlighted ? null : member.id
                          );
                        }}
                      >
                        Assigned shifts
                      </Button>
                    )}
                    {canHighlightEligible && (
                      <Button
                        kind={isHighlighted ? 'primary' : 'secondary'}
                        size="sm"
                        onClick={(event) => {
                          event.stopPropagation();
                          onHighlightWorker?.(isHighlighted ? null : member.id);
                        }}
                      >
                        Eligible shifts
                      </Button>
                    )}
                  </Gap>
                </Gutter>
              )}
            </Gap>
          </Gutter>
        </Gap>
      </div>
    </Card>
  );
}
